import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@xanectech/ui/components';
import { useUpdateMessage } from '@xanectech/data';
import type { Message } from '@xanectech/types';
import { useState, useEffect } from 'react';

import { createLogger } from '@xanectech/lib';

const logger = createLogger('Components - EditMessageDialog');

interface EditMessageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  message: Message;
}

export const EditMessageDialog = ({
  isOpen,
  onClose,
  message,
}: EditMessageDialogProps) => {
  const updateMessage = useUpdateMessage();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [type, setType] = useState<'info' | 'warning' | 'success' | 'error'>(
    'info',
  );

  useEffect(() => {
    if (message) {
      setTitle(message.title as string);
      setContent(message.content as string);
      setType(message.type as 'info' | 'warning' | 'success' | 'error');
    }
  }, [message]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    updateMessage
      .mutateAsync({
        messageId: message.id,
        title,
        content,
        type,
      })
      .then(() => {
        onClose();
      })
      .catch((error: unknown) => {
        logger.error('Failed to update message:', {
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
        });
      });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='sm:max-w-[600px]'>
        <DialogHeader>
          <DialogTitle>Edit Message</DialogTitle>
          <DialogDescription>
            Update the message details below
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className='space-y-4'>
          <div className='space-y-2'>
            <Label htmlFor='title'>Title</Label>
            <Input
              id='title'
              value={title}
              onChange={(e) => setTitle(String(e.target.value))}
              placeholder='Message title'
              required
            />
          </div>

          <div className='space-y-2'>
            <Label htmlFor='content'>Content</Label>
            <Textarea
              id='content'
              value={content}
              onChange={(e) => setContent(String(e.target.value))}
              placeholder='Message content'
              rows={4}
              required
            />
          </div>

          <div className='space-y-2'>
            <Label htmlFor='type'>Message Type</Label>
            <Select
              value={type}
              onValueChange={(
                value: 'info' | 'warning' | 'success' | 'error',
              ) => setType(value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='info'>Info</SelectItem>
                <SelectItem value='warning'>Warning</SelectItem>
                <SelectItem value='success'>Success</SelectItem>
                <SelectItem value='error'>Error</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type='button' variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button type='submit' disabled={updateMessage.isPending}>
              {updateMessage.isPending ? 'Updating...' : 'Update Message'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
